/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import * as _ from 'underscore';
import { appConstants } from '@app/common/app-constants';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpHeaders } from '@angular/common/http';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { UtilService } from '@app/util/util.service';
import { reportIdStatusMap } from '@app/di/integrations/reporting/reporting-contants';
const log = new Logger('AuditService');

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private auditEnabled = true;

  constructor(
    private appConfigService: AppConfigService,
    private currentExamContext: CurrentExamContextService,
    private dataService: ProxyDataService,
    private notify: NotifyService,
    private translate: TranslateService,
    private userPreferenceService: UserPreferenceService
  ) {}

  getMRN(mrnDomainArray) {
    let index;
    let len;
    let auditMrnWithDomain = null;
    let auditMrn = null;

    if (!mrnDomainArray) {
      return auditMrn;
    }

    for (index = 0, len = mrnDomainArray.length; index < len; ++index) {
      const mrn = mrnDomainArray[index].value;
      const domain = mrnDomainArray[index].system;
      log.debug(mrn + '^^^' + domain);
      // first one with both mrn and domain
      if (mrn !== '' && domain !== '') {
        auditMrnWithDomain = mrn + '^^^' + domain;
        return auditMrnWithDomain;
      }

      // get the first mrn when none of the domains have an mrn
      if (auditMrn === null && mrn !== '') {
        auditMrn = mrn;
      }
    }
    return auditMrn;
  }

  getEMRDomainMRN(mrnDomainArray) {
    let index;
    let len;
    let auditMrnWithDomain = null;
    const auditMrn = null;
    const EMRDomain = this.appConfigService.getIRCCEmrDomain();

    if (!mrnDomainArray) {
      return auditMrn;
    }

    if (EMRDomain !== '') {
      for (index = 0, len = mrnDomainArray.length; index < len; ++index) {
        const mrn = mrnDomainArray[index].value;
        const domain = mrnDomainArray[index].system;
        // get mrn and domain based on configuration
        if (mrn !== '' && domain !== '' && domain === EMRDomain) {
          auditMrnWithDomain = mrn + '^^^' + domain;
          return auditMrnWithDomain;
        }

        // get the first mrn when invalid domain is configured
        if (auditMrnWithDomain === null) {
          return this.getMRN(mrnDomainArray);
        }
      }
    } else {
      return this.getMRN(mrnDomainArray);
    }
    return auditMrn;
  }

  generateParticipant(type, value, text?: string) {
    const participant = { type, value, text };
    if (text) {
      participant.text = text;
    }
    return participant;
  }

  // update audit info with extra params
  updateAuditParams(examAuditInfo, params) {
    let participant;
    if (examAuditInfo && examAuditInfo.participants) {
      if (examAuditInfo.ownerName) {
        participant = this.generateParticipant('user', examAuditInfo.ownerName);
        examAuditInfo.participants.push(participant);
      }
      if (examAuditInfo.actor) {
        params.actor = examAuditInfo.actor;
      }
      if (examAuditInfo.comment) {
        params.comment = examAuditInfo.comment;
      }
      params.participants = examAuditInfo.participants;
    }
    return params;
  }

  // this method return the system name based on user preferences
  getActorOnExamRelease(preferenceKey) {
    const preferenceValue = this.userPreferenceService.getUserPreferenceFromCache(preferenceKey);
    return preferenceValue === 'release' ? '*system*' : this.appConfigService.getUserId();
  }

  processExamAuditInfo(item, participantsDTO) {
    if (item) {
      let mrnParticipant;
      let examIdParticipant;
      let mrn;
      let accession;

      if (item.keys) {
        mrn = this.getMRN(item.keys.mrn);
        accession = item.keys.procedureAccessionNo;
        mrnParticipant = this.generateParticipant('patient', item.keys.patientId, mrn);
        examIdParticipant = this.generateParticipant('exam', item.id, accession);
        participantsDTO.push(examIdParticipant, mrnParticipant);
      } else {
        const mrnDomainArr = [];
        accession = item.accession ? item.accession : item.procedureAccessionNo;
        log.debug('accession: ' + accession);
        if (item.mrn && item.mrn.domainIdentifier && item.mrn.domainIdentifierName) {
          mrnDomainArr.push({
            system: item.mrn.domainIdentifierName,
            value: item.mrn.domainIdentifier
          });
        }
        if (item.mrn && item.mrn.otherDomainIdentifierList) {
          _.each(item.mrn.otherDomainIdentifierList, otherDomainIdentifier => {
            mrnDomainArr.push(otherDomainIdentifier);
          });
        }
        if (item.mrn && !item.mrn.domainIdentifier && !item.mrn.otherDomainIdentifierList) {
          _.each(item.mrn, otherDomainIdentifier => {
            mrnDomainArr.push(otherDomainIdentifier);
          });
        }
        mrn = this.getMRN(mrnDomainArr);
        log.debug('MRN: ' + mrn);

        examIdParticipant = this.generateParticipant('exam', item.id, accession);
        mrnParticipant = this.generateParticipant('patient', item.patientId, mrn);
        participantsDTO.push(examIdParticipant, mrnParticipant);
      }
    }
  }

  getAuditMRNWithDomain(mrndomainarray) {
    return this.getMRN(mrndomainarray);
  }

  isAuditEnabled() {
    const auditInfo = this.appConfigService.getAppConfigBySubCategory(appConstants.configSubCategory.AUDIT);
    this.auditEnabled = auditInfo.auditEnabled === 'true';
    log.debug('Audit: get Audit Status - ' + this.auditEnabled);

    return this.auditEnabled;
  }

  getActionCode() {
    return appConstants.auditActionCodes;
  }

  getActionType() {
    return appConstants.auditActionTypes;
  }

  getEventOutcome() {
    return appConstants.auditEventOutcome;
  }

  saveIRCCAuditAction(actionCodeValue, actionTypeValue, eventOutcomeValue, documentId) {
    if (this.auditEnabled) {
      if (this.currentExamContext !== null && this.auditEnabled) {
        log.debug('Save audit');

        const context = this.currentExamContext.getFullContext();

        if (context) {
          const auditInfo = this.getExamAuditInfo(context);
          const params = {
            eventActionCode: actionCodeValue,
            eventActionType: actionTypeValue,
            eventOutcome: eventOutcomeValue,
            participants: auditInfo.participants
          };
          params.participants = auditInfo.participants;
          params.participants.push(this.generateParticipant('document', '', documentId));
          this.saveAudit(params);
        } else {
          log.error('Error in Auditing:  no context found.');
        }
      }
    }
  }

  saveReportAuditAction(
    actionCodeValue,
    actionTypeValue,
    eventOutcomeValue,
    reportId,
    particpantName,
    reportStatus?,
    reason?,
    auditData?,
    examId?
  ) {
    if (this.currentExamContext !== null && this.auditEnabled) {
      log.debug('Save report audit');

      let context = this.currentExamContext.getFullContext();
      if (!context) {
        context = this.currentExamContext.getPreviousFullContext();
      }
      const comment = this.getCommentByActionCode(actionTypeValue, reportId, reportStatus, particpantName);
      if (auditData) {
        const params = this.getReportAuditParams(
          actionCodeValue,
          actionTypeValue,
          eventOutcomeValue,
          reason,
          comment,
          auditData.participants,
          particpantName,
          reportId
        );
        this.saveAudit(params);
      } else if (context) {
        if (context.id == examId) {
          const auditInfo = this.getExamAuditInfo(context);
          const params = this.getReportAuditParams(
            actionCodeValue,
            actionTypeValue,
            eventOutcomeValue,
            reason,
            comment,
            auditInfo.participants,
            particpantName,
            reportId
          );
          this.saveAudit(params);
        }
      } else {
        log.error('Error in report Auditing:  no context found.');
      }
    }
  }

  saveAuditAction(actionCodeValue, actionTypeValue, eventOutcomeValue, examAuditInfo?) {
    if (this.auditEnabled) {
      if (this.currentExamContext !== null && this.auditEnabled) {
        let params;
        log.debug('Save audit');

        const context = this.currentExamContext.getFullContext();

        // we don't support audit for remote exam , we will send the request only if the exam is local
        if (examAuditInfo && examAuditInfo.participants && !examAuditInfo.isRemoteStudy) {
          params = {
            eventActionCode: actionCodeValue,
            eventActionType: actionTypeValue,
            eventOutcome: eventOutcomeValue
          };
          params = this.updateAuditParams(examAuditInfo, params);
          this.saveAudit(params);
        } else if (context) {
          // when the examAudit info is undefined , we need to get the audit information from the context
          const auditInfo = this.getExamAuditInfo(context);
          if (!auditInfo.isRemoteStudy) {
            if (!examAuditInfo) {
              examAuditInfo = auditInfo;
            } else {
              examAuditInfo.participants = auditInfo.participants;
            }
            params = {
              eventActionCode: actionCodeValue,
              eventActionType: actionTypeValue,
              eventOutcome: eventOutcomeValue
            };
            params = this.updateAuditParams(examAuditInfo, params);
            this.saveAudit(params);
          }
        } else {
          log.error('Error in Auditing:  no context found.');
        }
      }
    }
  }

  saveAudit(auditParams) {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' })
    };
    this.dataService.httpPut(appConstants.appURLs.auditEvent, auditParams, httpOptions).then(
      () => {
        log.debug('Audit Saved : audit info: ', auditParams);
      },
      error => {
        // due to backend switch turned off or error condition (RabbitMQ down),
        // no longer attempt to audit this session
        if (error.status === 500) {
          this.auditEnabled = false;
          log.error('Error in Auditing:  server error or auditing is disabled.');
          this.notify.error(this.translate.instant('imagingDesktop.error.auditDisableMsg'));
        } else if (error.status === 400) {
          if (!!error.data && error.data.errorMessage === 'RabbitMQ_Down') {
            log.error('Error in Auditing:  RabbitMQ is down.');
            this.notify.error(this.translate.instant('imagingDesktop.error.auditDisableMsg'));
          }
        }
      }
    );
  }

  getExamAuditInfo(item) {
    const participantsDTO = [];
    if (item && item.length > 1) {
      item.forEach(workItem => {
        this.processExamAuditInfo(workItem.workItem, participantsDTO);
      });
    } else if (item.workItem) {
      this.processExamAuditInfo(item.workItem, participantsDTO);
    } else {
      this.processExamAuditInfo(item, participantsDTO);
    }

    const examAuditInfo = {
      participants: participantsDTO,
      isRemoteStudy: false,
      actor: ''
    };
    examAuditInfo.isRemoteStudy = UtilService.isRemoteExam(item);
    log.debug('exam Audit Information ', JSON.stringify(examAuditInfo));
    return examAuditInfo;
  }

  extractAuditInfoFromExam(exam) {
    const examAuditInfo = {} as any;
    const mrnDomainArr = [];
    if (exam.mrn && exam.mrn.domainIdentifier && exam.mrn.domainIdentifierName) {
      mrnDomainArr.push({ system: exam.mrn.domainIdentifierName, value: exam.mrn.domainIdentifier });
    }
    if (exam.mrn && exam.mrn.otherDomainIdentifierList) {
      _.each(exam.mrn.otherDomainIdentifierList, otherDomainIdentifier => {
        mrnDomainArr.push(otherDomainIdentifier);
      });
    }
    if (exam.mrn && !exam.mrn.domainIdentifier && !exam.mrn.otherDomainIdentifierList) {
      _.each(exam.mrn, otherDomainIdentifier => {
        mrnDomainArr.push(otherDomainIdentifier);
      });
    }
    examAuditInfo.mrn = this.getAuditMRNWithDomain(mrnDomainArr);
    examAuditInfo.accessionNumber = exam.accession ? exam.accession : exam.procedureAccessionNo;
    examAuditInfo.patientId = exam.patientId;
    return examAuditInfo;
  }

  getReportAuditParams(
    actionCodeValue,
    actionTypeValue,
    eventOutcomeValue,
    reason,
    comment,
    participants,
    particpantName,
    reportId
  ) {
    const params = {
      eventActionCode: actionCodeValue,
      eventActionType: actionTypeValue,
      eventOutcome: eventOutcomeValue,
      participants: participants,
      comment: reason
    };
    if (!reason) {
      params.comment = comment;
    }
    params.participants.push(this.generateParticipant('application server', '', particpantName));
    params.participants.push(this.generateParticipant('document', '', reportId));
    return params;
  }

  getCommentByActionCode(actionTypeValue, reportId, reportStatus, applicationServer) {
    let comment: string;
    let status;
    if (reportStatus) {
      status = reportIdStatusMap[reportStatus];
    }
    switch (actionTypeValue) {
      case appConstants.auditActionTypes.ReportCreation:
        comment = `Report ${reportId} has been created in ${status} status`;
        break;
      case appConstants.auditActionTypes.ReportCreationOnLegacy:
        comment = `Report ${reportId} has been created in ${status} status on a Legacy report`;
        break;
      case appConstants.auditActionTypes.ReportCreationFailure:
        comment = `Report can't be created on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.ReportCreationOnLegacyFailure:
        comment = `Report can't be created for legacy on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.ReportDiscard:
        comment = `Report ${reportId} has been discarded`;
        break;
      case appConstants.auditActionTypes.ReportEmergencyDiscard:
        comment = `Report ${reportId} has been discarded in emergency`;
        break;
      case appConstants.auditActionTypes.ReportEmergencyDiscardFailure:
        comment = `Report ${reportId} can't be discarded on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.ReportDeletion:
        comment = `Report ${reportId} has been deleted`;
        break;
      case appConstants.auditActionTypes.ReportDeletionFailure:
        comment = `Report ${reportId} can't be deleted on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.ReportSave:
        comment = `Report ${reportId} has been saved in ${status} status`;
        break;
      case appConstants.auditActionTypes.ReportSaveFailure:
        comment = `Report ${reportId} can't be saved on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.ReportUpdation:
        comment = `Report ${reportId} has been linked to the current accession number`;
        break;
      case appConstants.auditActionTypes.ReportUpdationFailure:
        comment = `Report ${reportId} can't be updated on ${applicationServer}`;
        break;
      case appConstants.auditActionTypes.AddendumDeletion:
        comment = `Addendum has been deleted for  report ${reportId}`;
        break;
      case appConstants.auditActionTypes.AddendumDeletionFailure:
        comment = `Addendum for report ${reportId} can't be deleted on ${applicationServer}`;
        break;
      default:
        break;
    }
    return comment;
  }

  getAuditInfo(id: number): Promise<any> {
    return this.dataService.getById(appConstants.appURLs.auditEvent, `${id}`);
  }
}
